/**
 * @shell standard @header bar (guest browse only)
 * Order tab: menu listing + cart. Browse items, add/remove quantity, go to checkout.
 * Cart is persisted as bookMealData and restored on load.
 * Item rows align visually with food menu cards (shared dimensions from `foodMenu/.../menuFlowItemCardShared`).
 */
import { ConditionalSolidLayer } from '../components/ui/ConditionalSolidLayer';
import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Image, RefreshControl, Platform, Alert, FlatList } from 'react-native';
import type { BlurTint } from '../shared/theme/blurParity';
import Animated, { useSharedValue, useAnimatedStyle, useAnimatedProps, withSpring, interpolate, Extrapolation } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useFeatureGateExit } from '../shared/navigation/useFeatureGateExit';
import { SvgIcon } from '../components/common/SvgIcon';
import { MainLoader } from '../components/load-main/MainLoader';
import { ScreenHeader, useHeaderTopPadding, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { Clickable } from '../components/common/Clickable';
import { ErrorState } from '../components/common/ErrorState';
import { appTheme, useThemeColors, useThemeStore, type ThemeColors } from '../shared/theme';
import {
  createStackedFooterPrimaryLabelStyle,
  createStackedFooterPrimarySurfaceStyle,
  blurIntensityForPlatform,
  FROSTED_BOTTOM_CHROME_RADIUS,
} from '../shared/theme/sharedStyles';
import { useAddresses } from '../data/domains/addresses';
import { useOrderMenuQuery } from '../data/domains/orders';
import { useOnlineOrdersFeatureEnabled } from '../data/domains/config';
import { useAuthStore } from '../shared/auth/store';
import { useTranslation, getApiText } from '../shared/i18n';
import { useRtl } from '../shared/i18n/useRtl';
import { resolveImageUri } from '../shared/utils/resolveImageUri';
import { formatCurrency } from '../shared/utils/currencyUtils';
import {
  getBookMealData,
  setBookMealData,
  setShopSettings,
  setCheckoutSlug,
  getCheckoutSlug,
  clearCheckoutSlug,
  type StoredCartItem,
} from '../data/domains/orders';
import { Toast } from '../components/common/Toast';
import {
  MENU_FLOW_ITEM_CARD_BORDER_WIDTH,
  MENU_FLOW_ITEM_CARD_HEIGHT,
  MENU_FLOW_ITEM_CARD_INSET,
  MENU_FLOW_ITEM_CARD_MARGIN_VERTICAL,
  MENU_FLOW_ITEM_CONTENT_PADDING_H,
  MENU_FLOW_ITEM_CONTENT_PADDING_V,
  MENU_FLOW_ITEM_DESC_FONT_SIZE,
  MENU_FLOW_ITEM_DESCRIPTION_MARGIN_TOP,
  MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS,
  MENU_FLOW_ITEM_IMAGE_SECTION_WIDTH,
  MENU_FLOW_ITEM_IMAGE_WIDTH,
  MENU_FLOW_ITEM_STEPPER_BTN_SIZE,
  MENU_FLOW_ITEM_STEPPER_HEIGHT,
  MENU_FLOW_ITEM_STEPPER_VALUE_WIDTH,
  MENU_FLOW_ITEM_TITLE_FONT_SIZE,
  MENU_FLOW_ITEM_TITLE_MIN_HEIGHT,
  MEAL_SNACK_TAG_EXPAND_BTN_RADIUS,
  MEAL_SNACK_TAG_EXPAND_BTN_SIZE,
  MEAL_SNACK_TAG_EXPAND_ICON_SIZE,
} from '../foodMenu/components/menuFlowItemCardShared';
import { MENU_FLOW_BOTTOM_OVERLAY_PADDING_TOP, MENU_FLOW_BOTTOM_OVERLAY_PADDING_HORIZONTAL } from '../foodMenu/components/MenuFlowBottomMacroBar';
import { horizontalLtrDirectionStyle } from '../shared/i18n/rtl';
import {
  getAndroidFloatingTabBarLayoutHeight,
  getMenuFlowBottomOverlayPaddingBottom,
} from '../navigation/mainTabs/androidFloatingTabBarLayout';
import {
  ScreenShell,
  STACK_SCROLL_GUTTER,
  studioCardShell,
  DASH_STUDIO_RADIUS,
} from '../shared/theme/studio/stackScreenShell';
import { SCROLL_VIEW_PROPS, MENU_FLOW_FLAT_LIST_PROPS } from '../shared/utils/scrollListPerf';

const ORDER_CART_BAR_BLUR_MAX_INTENSITY = blurIntensityForPlatform(9);
const AnimatedFlatList = Animated.createAnimatedComponent(FlatList<CartItem>);
const ORDER_ROW_STRIDE = MENU_FLOW_ITEM_CARD_HEIGHT + MENU_FLOW_ITEM_CARD_MARGIN_VERTICAL * 2;

export interface CartItem {
  id: number;
  name: string;
  description?: string;
  attachment?: string;
  image?: string;
  price: number;
  quantity: number;
}

const STEPPER_BTN = MENU_FLOW_ITEM_STEPPER_BTN_SIZE;

function createOrderScreenStyles(c: ThemeColors) {
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: 'transparent' },
    center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
    loadingText: { marginTop: 12, ...appTheme.typography.body, color: c.textSecondary },
    headerRightBtnWrap: {
      maxWidth: '100%',
    },
    headerRightBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingVertical: 8,
      paddingHorizontal: appTheme.spacing.sm,
      maxWidth: '100%',
    },
    historyLink: {
      ...appTheme.typography.bodySmall,
      color: c.primary,
      fontWeight: '600',
      flexShrink: 1,
    },
    errorText: { ...appTheme.typography.body, color: c.textSecondary, marginBottom: 16 },
    retryBtnWrap: {},
    retryBtnInner: { gap: appTheme.spacing.sm },
    scroll: { flex: 1 },
    scrollContent: { paddingHorizontal: STACK_SCROLL_GUTTER },
    emptyState: { alignItems: 'center', paddingVertical: 48 },
    emptyTitle: { ...appTheme.typography.h3, color: c.text, marginTop: 16 },
    emptySubtitle: { ...appTheme.typography.bodySmall, color: c.textSecondary, marginTop: 8, textAlign: 'center', paddingHorizontal: 24 },
    /** Matches food menu `MenuFlowItemCard` shell + geometry. */
    itemCard: {
      flexDirection: 'row',
      alignItems: 'stretch',
      height: MENU_FLOW_ITEM_CARD_HEIGHT,
      marginVertical: MENU_FLOW_ITEM_CARD_MARGIN_VERTICAL,
      overflow: 'hidden',
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      borderWidth: MENU_FLOW_ITEM_CARD_BORDER_WIDTH,
      borderColor: 'transparent',
      padding: MENU_FLOW_ITEM_CARD_INSET,
      gap: MENU_FLOW_ITEM_CARD_INSET,
      ...horizontalLtrDirectionStyle,
    },
    itemCardSelected: {
      borderColor: c.primary,
    },
    imageSection: {
      width: MENU_FLOW_ITEM_IMAGE_SECTION_WIDTH,
      alignSelf: 'center',
      justifyContent: 'center',
    },
    imageInner: {
      width: MENU_FLOW_ITEM_IMAGE_WIDTH,
      height: MENU_FLOW_ITEM_IMAGE_WIDTH,
      borderRadius: MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS,
      overflow: 'hidden',
      backgroundColor: c.borderLight,
    },
    itemImage: {
      width: '100%',
      height: '100%',
    },
    itemImagePlaceholder: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: c.primaryUltra,
    },
    detailExpandBtn: {
      position: 'absolute',
      bottom: 0,
      right: 0,
      width: MEAL_SNACK_TAG_EXPAND_BTN_SIZE,
      height: MEAL_SNACK_TAG_EXPAND_BTN_SIZE,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      borderTopLeftRadius: MEAL_SNACK_TAG_EXPAND_BTN_RADIUS,
      borderBottomLeftRadius: 0,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 4,
    },
    itemBody: {
      flex: 1,
      minWidth: 0,
      justifyContent: 'space-between',
      overflow: 'hidden',
    },
    itemTextBlock: {
      flex: 1,
      justifyContent: 'flex-start',
      minWidth: 0,
      paddingTop: MENU_FLOW_ITEM_CONTENT_PADDING_V,
      paddingHorizontal: MENU_FLOW_ITEM_CONTENT_PADDING_H,
    },
    itemName: {
      fontSize: MENU_FLOW_ITEM_TITLE_FONT_SIZE,
      fontWeight: '600',
      color: c.text,
      minHeight: MENU_FLOW_ITEM_TITLE_MIN_HEIGHT,
    },
    itemDesc: {
      fontSize: MENU_FLOW_ITEM_DESC_FONT_SIZE,
      color: c.textSecondary,
      marginTop: MENU_FLOW_ITEM_DESCRIPTION_MARGIN_TOP,
    },
    itemRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: MENU_FLOW_ITEM_CONTENT_PADDING_H,
      paddingBottom: MENU_FLOW_ITEM_CONTENT_PADDING_V,
      minHeight: MENU_FLOW_ITEM_STEPPER_HEIGHT,
      ...horizontalLtrDirectionStyle,
    },
    itemPrice: {
      fontSize: MENU_FLOW_ITEM_TITLE_FONT_SIZE,
      color: c.primary,
      fontWeight: '700',
    },
    stepper: {
      flexDirection: 'row',
      alignItems: 'center',
      height: MENU_FLOW_ITEM_STEPPER_HEIGHT,
      borderRadius: MENU_FLOW_ITEM_STEPPER_BTN_SIZE / 2,
      overflow: 'hidden',
    },
    stepperBtnWrap: { width: STEPPER_BTN, height: STEPPER_BTN },
    stepperBtn: {
      width: STEPPER_BTN,
      height: STEPPER_BTN,
      borderRadius: STEPPER_BTN / 2,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: c.primary,
    },
    stepperQty: {
      minWidth: MENU_FLOW_ITEM_STEPPER_VALUE_WIDTH,
      fontSize: 12,
      fontWeight: '700',
      color: c.onPrimary,
      textAlign: 'center',
    },
    addBtnWrap: {},
    addBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      height: MENU_FLOW_ITEM_STEPPER_HEIGHT,
      paddingHorizontal: 10,
      borderRadius: MENU_FLOW_ITEM_STEPPER_BTN_SIZE / 2,
      backgroundColor: c.primary,
    },
    addBtnText: { fontSize: 12, fontWeight: '600', color: c.onPrimary },
    /** Same shell as `MenuFlowBottomMacroBar`: full-bleed bottom, above tab via padding. */
    orderBarCard: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 99,
      elevation: 16,
      flexDirection: 'column',
    },
    orderBarPadding: {
      paddingTop: MENU_FLOW_BOTTOM_OVERLAY_PADDING_TOP,
      paddingHorizontal: MENU_FLOW_BOTTOM_OVERLAY_PADDING_HORIZONTAL,
    },
    orderBarRow: {
      position: 'relative',
      zIndex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: appTheme.spacing.md,
      minHeight: 52,
    },
    orderBarLeft: { gap: 2, flex: 1, minWidth: 0 },
    orderBarCount: { ...appTheme.typography.caption, color: c.textSecondary },
    orderBarTotal: { ...appTheme.typography.h3, color: c.primary, fontWeight: '700' },
    checkoutBtnShrink: { flexShrink: 0 },
  });
}

export type OrderScreenProps = {
  /** AuthStack guest `Order` route uses `'main'`; guest browse from welcome uses the default. */
  guest_checkout_landing?: 'main' | 'browse';
};

export const OrderScreen: React.FC<OrderScreenProps> = ({ guest_checkout_landing = 'browse' }) => {
  const navigation = useAuthNavigation();
  const { t } = useTranslation();
  const rtl = useRtl();
  const insets = useSafeAreaInsets();
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createOrderScreenStyles(themeColors), [themeColors]);
  const checkout_primary = useMemo(
    () => ({
      surface: createStackedFooterPrimarySurfaceStyle(themeColors),
      label: createStackedFooterPrimaryLabelStyle(themeColors),
    }),
    [themeColors],
  );
  const user = useAuthStore(s => s.user);
  const show_online_orders = useOnlineOrdersFeatureEnabled();
  const { data: addressesData } = useAddresses();
  const addresses = Array.isArray(addressesData) ? addressesData : [];
  const defaultAddress = addresses.find((a: any) => a.is_default) ?? addresses[0];

  const { data: orderMenuData, isLoading, error, refetch } = useOrderMenuQuery();
  const [refreshing, setRefreshing] = useState(false);

  const allItems = orderMenuData?.allItems ?? [];
  const shopSettings = orderMenuData?.shopSettings ?? null;

  const [cart, setCart] = useState<CartItem[]>([]);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const hasRestoredCartRef = useRef(false);

  useEffect(() => {
    if (allItems.length === 0 || hasRestoredCartRef.current) {
      return;
    }
    let mounted = true;
    getBookMealData().then(stored => {
      if (!mounted || !stored?.length) {
        hasRestoredCartRef.current = true;
        return;
      }
      const byId = new Map(stored.map((s: StoredCartItem) => [s.id, s]));
      const restored: CartItem[] = [];
      for (const item of allItems) {
        const s = byId.get(item.id);
        if (s && s.quantity > 0) {
          restored.push({ ...item, quantity: s.quantity });
        }
      }
      setCart(restored);
      hasRestoredCartRef.current = true;
    });
    return () => {
      mounted = false;
    };
  }, [allItems]);

  useEffect(() => {
    const selected = cart
      .filter(c => (c.quantity || 0) > 0)
      .map(c => ({
        id: c.id,
        quantity: c.quantity || 1,
        price: c.price,
        name: c.name,
        description: c.description,
        attachment: c.attachment,
        image: c.image,
        a_name: (c as any).a_name,
        a_description: (c as any).a_description,
      }));
    if (selected.length > 0) {
      setBookMealData(selected);
    }
  }, [cart]);

  useFocusEffect(
    useCallback(() => {
      getBookMealData().then(stored => {
        if (!stored || stored.length === 0) {
          setCart([]);
        }
      });
      getCheckoutSlug().then(slug => {
        if (slug !== 'order') {
          return;
        }
        clearCheckoutSlug();
        if (guest_checkout_landing === 'main') {
          return;
        }
        getBookMealData().then(stored => {
          navigation.navigate('OrderCheckout', { items: stored?.length ? stored : [] });
        });
      });
    }, [navigation, guest_checkout_landing]),
  );

  const guest_from_main = guest_checkout_landing === 'main';
  const { scrollY, onScroll } = useScreenHeaderScroll();
  /**
   * Absolute stack `ScreenHeader` with reveal chrome — content must clear safe-area + bar height.
   * Do not use `useHubHeaderTopPadding` here (that skips iOS safe-area for collapsing hubs).
   */
  const content_top = useHeaderTopPadding(false);
  const on_back_exit = useExitOrGoBack();
  const go_main = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    (navigation as { navigate: (name: string) => void }).navigate('Main');
  }, [navigation]);
  const orders_enabled = useFeatureGateExit(show_online_orders, guest_from_main ? go_main : on_back_exit);

  const tab_bar_height = guest_from_main
    ? Math.max(insets.bottom, appTheme.spacing.md)
    : Platform.OS === 'android'
      ? getAndroidFloatingTabBarLayoutHeight(insets.bottom)
      : 56 + insets.bottom / 2;
  const order_bar_bottom_padding = guest_from_main
    ? Math.max(insets.bottom, appTheme.spacing.md)
    : getMenuFlowBottomOverlayPaddingBottom(insets.bottom);
  const order_cart_bar_estimated_height =
    MENU_FLOW_BOTTOM_OVERLAY_PADDING_TOP + 56 + order_bar_bottom_padding + (Platform.OS === 'android' ? 0 : 16);
  const appearance = useThemeStore(s => s.appearance);
  const cart_bar_blur_tint: BlurTint = appearance === 'dark' ? 'systemChromeMaterialDark' : 'systemChromeMaterialLight';
  const cartCount = useMemo(() => cart.reduce((s, i) => s + (i.quantity || 0), 0), [cart]);
  const cart_bar_visible = cartCount > 0;
  const cart_transition = useSharedValue(0);

  useEffect(() => {
    cart_transition.value = withSpring(cart_bar_visible ? 1 : 0, {
      damping: 20,
      stiffness: 100,
    });
  }, [cart_bar_visible]);

  const animated_cart_bar_style = useAnimatedStyle(() => ({
    opacity: cart_transition.value,
    transform: [{ translateY: interpolate(cart_transition.value, [0, 1], [50, 0], Extrapolation.CLAMP) }],
  }));

  const scroll_bottom_padding = useMemo(
    () => (cartCount > 0 ? order_cart_bar_estimated_height + 8 : tab_bar_height + (guest_from_main ? appTheme.spacing.sm : 100)),
    [cartCount, order_cart_bar_estimated_height, tab_bar_height, guest_from_main],
  );
  const cartTotal = useMemo(() => cart.reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0), [cart]);

  const addToCart = useCallback((item: CartItem) => {
    setCart(prev => {
      const existing = prev.find(p => p.id === item.id);
      if (existing) {
        return prev.map(p => (p.id === item.id ? { ...p, quantity: p.quantity + 1 } : p));
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  }, []);

  const removeFromCart = useCallback((item: CartItem) => {
    setCart(prev => {
      const existing = prev.find(p => p.id === item.id);
      if (!existing || existing.quantity <= 0) {
        return prev;
      }
      if (existing.quantity === 1) {
        return prev.filter(p => p.id !== item.id);
      }
      return prev.map(p => (p.id === item.id ? { ...p, quantity: p.quantity - 1 } : p));
    });
  }, []);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    refetch?.().finally(() => setRefreshing(false));
  }, [refetch]);

  const goToCheckout = useCallback(() => {
    const items = cart.filter(c => (c.quantity || 0) > 0).map(c => ({ ...c, quantity: c.quantity || 1 }));
    if (items.length === 0) {
      setToastMessage(t('order.selectAtLeastOneItem'));
      return;
    }
    const stored = items.map(i => ({
      id: i.id,
      quantity: i.quantity || 1,
      price: i.price,
      name: i.name,
      description: i.description,
      attachment: i.attachment,
      image: i.image,
      a_name: (i as any).a_name,
      a_description: (i as any).a_description,
    }));
    setBookMealData(stored);
    const min_cost = shopSettings?.minimum_order_cost ?? 0;
    setShopSettings({ minimum_order_cost: min_cost }).catch(() => {});
    if (!user) {
      setCheckoutSlug('order');
      navigation.navigate('Main');
      return;
    }
    if (addresses.length === 0) {
      Alert.alert(t('checkout.addressRequired'), t('checkout.addAddressMessage'), [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('checkout.addNew'), onPress: () => navigation.navigate('AddressForm') },
      ]);
      return;
    }
    navigation.navigate('OrderCheckout', { items: stored });
  }, [cart, user, shopSettings, navigation, t, addresses.length, guest_checkout_landing]);

  const qty_by_id = useMemo(() => {
    const map = new Map<number, number>();
    for (const c of cart) {
      map.set(c.id, c.quantity || 0);
    }
    return map;
  }, [cart]);

  const render_order_item = useCallback(
    ({ item }: { item: CartItem }) => {
      const qty = qty_by_id.get(item.id) ?? 0;
      const imgUri = resolveImageUri(item.attachment ?? item.image);
      const cart_item: CartItem = { ...item, quantity: qty };
      return (
        <View style={[styles.itemCard, qty > 0 && styles.itemCardSelected]}>
          <View style={styles.imageSection}>
            <View style={styles.imageInner}>
              {imgUri ? (
                <Image source={{ uri: imgUri }} style={styles.itemImage} resizeMode="cover" />
              ) : (
                <View style={styles.itemImagePlaceholder}>
                  <SvgIcon name="menu" size={32} color={themeColors.primary} />
                </View>
              )}
              <Clickable
                onPress={() => navigation.navigate('FoodItemDetail', { item })}
                accessibilityLabel={t('menu.openItemDetail')}
                accessibilityRole="button">
                <View
                  style={[
                    styles.detailExpandBtn,
                    { backgroundColor: appearance === 'light' ? themeColors.surface : 'rgba(0,0,0,0.75)' },
                  ]}>
                  <SvgIcon
                    name="expand"
                    size={MEAL_SNACK_TAG_EXPAND_ICON_SIZE}
                    color={appearance === 'light' ? themeColors.black : '#fff'}
                  />
                </View>
              </Clickable>
            </View>
          </View>
          <View style={styles.itemBody}>
            <View style={styles.itemTextBlock}>
              <Text style={[styles.itemName, rtl.leadingMediaCopyTextStyle]} numberOfLines={1}>
                {getApiText(item as unknown as Record<string, unknown>, 'name', 'a_name') || item.name || t('order.itemFallback')}
              </Text>
              {getApiText(item as unknown as Record<string, unknown>, 'description', 'a_description') || item.description ? (
                <Text style={[styles.itemDesc, rtl.leadingMediaCopyTextStyle]} numberOfLines={2}>
                  {getApiText(item as unknown as Record<string, unknown>, 'description', 'a_description') || item.description}
                </Text>
              ) : null}
            </View>
            <View style={styles.itemRow}>
              <Text style={[styles.itemPrice, rtl.textForceLtr]}>{formatCurrency(item.price || 0)}</Text>
              <View style={[styles.stepper, { backgroundColor: qty > 0 ? themeColors.primary : themeColors.surface }]}>
                {qty > 0 ? (
                  <>
                    <Clickable onPress={() => removeFromCart(cart_item)} accessibilityLabel={t('order.remove')} accessibilityRole="button">
                      <View style={styles.stepperBtnWrap}>
                        <View style={styles.stepperBtn}>
                          <SvgIcon name="minus" size={12} color={themeColors.onPrimary} />
                        </View>
                      </View>
                    </Clickable>
                    <Text style={styles.stepperQty}>{qty}</Text>
                    <Clickable onPress={() => addToCart(item)} accessibilityLabel={t('order.add')} accessibilityRole="button">
                      <View style={styles.stepperBtnWrap}>
                        <View style={styles.stepperBtn}>
                          <SvgIcon name="plus" size={12} color={themeColors.onPrimary} />
                        </View>
                      </View>
                    </Clickable>
                  </>
                ) : (
                  <Clickable onPress={() => addToCart(item)} accessibilityLabel={t('order.add')} accessibilityRole="button">
                    <View style={styles.addBtnWrap}>
                      <View style={styles.addBtn}>
                        <SvgIcon name="plus" size={12} color={themeColors.onPrimary} />
                        <Text style={styles.addBtnText}>{t('order.add')}</Text>
                      </View>
                    </View>
                  </Clickable>
                )}
              </View>
            </View>
          </View>
        </View>
      );
    },
    [
      addToCart,
      appearance,
      navigation,
      qty_by_id,
      removeFromCart,
      rtl.leadingMediaCopyTextStyle,
      rtl.textForceLtr,
      styles,
      t,
      themeColors.black,
      themeColors.onPrimary,
      themeColors.primary,
      themeColors.surface,
    ],
  );

  const key_extractor = useCallback((item: CartItem) => String(item.id), []);
  const get_item_layout = useCallback(
    (_: ArrayLike<CartItem> | null | undefined, index: number) => ({
      length: ORDER_ROW_STRIDE,
      offset: ORDER_ROW_STRIDE * index,
      index,
    }),
    [],
  );

  const list_empty = useMemo(
    () => (
      <View style={styles.emptyState}>
        <SvgIcon name="menu" size={56} color={themeColors.textSecondary} />
        <Text style={styles.emptyTitle}>{t('order.noItemsAvailable')}</Text>
        <Text style={styles.emptySubtitle}>{t('order.noItemsSubtitle')}</Text>
      </View>
    ),
    [styles, t, themeColors.textSecondary],
  );

  const goToOrderHistory = useCallback(() => {
    navigation.navigate('OrderHistory');
  }, [navigation]);

  const right_action = show_online_orders ? (
    <Clickable onPress={goToOrderHistory} accessibilityLabel={t('order.pastOrders')} accessibilityRole="button">
      <View style={styles.headerRightBtnWrap}>
        <View style={[styles.headerRightBtn, rtl.isRTL && { flexDirection: 'row-reverse' }]}>
          <Text style={styles.historyLink} numberOfLines={1} ellipsizeMode="tail">
            {t('order.pastOrders')}
          </Text>
          <SvgIcon name={rtl.chevronNextAlongAxis} size={18} color={themeColors.primary} />
        </View>
      </View>
    </Clickable>
  ) : undefined;

  const orders_header_side_slot = show_online_orders ? 128 : undefined;

  if (!orders_enabled) {
    return null;
  }

  if (isLoading && allItems.length === 0) {
    return (
      <ScreenShell colors={themeColors}>
        <View style={styles.container}>
          <ScreenHeader title={t('order.tabTitle')} rightAction={right_action} sideSlotWidth={orders_header_side_slot} onBack={guest_from_main ? go_main : undefined} scrollY={scrollY} />
          <View style={[styles.center, { paddingTop: content_top }]}>
            <MainLoader size={48} color={themeColors.primary} />
            <Text style={styles.loadingText}>{t('order.loadingMenu')}</Text>
          </View>
        </View>
      </ScreenShell>
    );
  }

  if (error) {
    return (
      <ScreenShell colors={themeColors}>
        <View style={styles.container}>
          <ScreenHeader title={t('order.tabTitle')} rightAction={right_action} sideSlotWidth={orders_header_side_slot} onBack={guest_from_main ? go_main : undefined} scrollY={scrollY} />
          <View style={{ flex: 1, paddingTop: content_top }}>
            <ErrorState
              title={t('order.couldNotLoadMenu')}
              message={t('common.pleaseTryAgain')}
              onRetry={() => refetch?.()}
              error={error}
              issue_context={{
                page: t('order.tabTitle'),
                issue: 'Failed to load order menu',
                kind: 'api',
                api_endpoint: 'menu_items',
                http_method: 'GET',
              }}
            />
          </View>
        </View>
      </ScreenShell>
    );
  }

  return (
    <ScreenShell colors={themeColors}>
    <View style={styles.container}>
      <ScreenHeader title={t('order.tabTitle')} rightAction={right_action} sideSlotWidth={orders_header_side_slot} onBack={guest_from_main ? go_main : undefined} scrollY={scrollY} />
      <AnimatedFlatList
        {...SCROLL_VIEW_PROPS}
        {...MENU_FLOW_FLAT_LIST_PROPS}
        style={styles.scroll}
        data={allItems}
        keyExtractor={key_extractor}
        renderItem={render_order_item}
        getItemLayout={get_item_layout}
        ListEmptyComponent={list_empty}
        onScroll={onScroll}
        scrollEventThrottle={16}
        contentContainerStyle={[styles.scrollContent, { paddingTop: content_top, paddingBottom: scroll_bottom_padding }]}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={themeColors.primary}
            progressViewOffset={content_top}
          />
        }
      />

      <Animated.View
        style={[styles.orderBarCard, styles.orderBarPadding, { paddingBottom: order_bar_bottom_padding }, animated_cart_bar_style]}
        pointerEvents={cart_bar_visible ? 'auto' : 'none'}
        accessibilityElementsHidden={!cart_bar_visible}
        importantForAccessibility={cart_bar_visible ? 'yes' : 'no-hide-descendants'}>
        <ConditionalSolidLayer
          transition={cart_transition}
          backgroundColor={themeColors.surface}
          style={[StyleSheet.absoluteFill, FROSTED_BOTTOM_CHROME_RADIUS]}
        />
        <View style={styles.orderBarRow}>
          <View style={styles.orderBarLeft}>
            <Text style={styles.orderBarCount}>
              {cartCount} {cartCount === 1 ? t('order.item') : t('order.items')}
            </Text>
            <Text style={[styles.orderBarTotal, rtl.textForceLtr]}>{formatCurrency(cartTotal)}</Text>
          </View>
          <Clickable onPress={goToCheckout} haptics={true} accessibilityLabel={t('order.checkout')} accessibilityRole="button" style={styles.checkoutBtnShrink}>
            <View
              style={[
                checkout_primary.surface,
                { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: appTheme.spacing.sm, paddingHorizontal: appTheme.spacing.md, minHeight: 48 },
              ]}>
              <Text style={checkout_primary.label}>{t('order.checkout')}</Text>
              <SvgIcon name="arrow-forward" size={20} color={themeColors.onPrimary} />
            </View>
          </Clickable>
        </View>
      </Animated.View>

      <Toast message={toastMessage ?? ''} visible={!!toastMessage} onHide={() => setToastMessage(null)} />
    </View>
    </ScreenShell>
  );
};

export default OrderScreen;
